import type { RuleId } from '~/rules/version'
import type { IsoTimestamp } from '~/types/dates'
import type { OpinionId, WeeklyReviewId } from '~/types/ids'
import type { RecordEnvelope } from './common'

/**
 * The engine's output (rules-engine.md, "Output"; D-046 adds `watchItems`).
 *
 * Note what is NOT here: any formatted string intended for display, and any
 * number rounded for presentation. Rounding happens only during serialization
 * (D-039), and display formatting only at the presentation boundary (D-041).
 */

/**
 * Confirmed recommendation families (rules-engine.md).
 *
 * `PhaseRulesUnavailable` is deliberately NOT a member. It is not a
 * recommendation, it carries no primary action and no confidence, and it is
 * never exported as an opinion (D-043) — so it is a separate outcome type
 * rather than a category in this union.
 */
export const RECOMMENDATION_CATEGORIES = [
  'stayTheCourse',
  'gatherMoreData',
  'improveAdherenceFirst',
  'considerOneSmallAdjustment',
  'easeTheCurrentPhase',
  'doNotAdjustYet',
  'endOrTransitionThePhase',
] as const

export type RecommendationCategory = (typeof RECOMMENDATION_CATEGORIES)[number]

export type Confidence = 'low' | 'moderate' | 'high'

/**
 * A single observed fact. Never a judgement — "weight fell 0.4 kg" belongs
 * here, "that is too fast" belongs in the recommendation.
 *
 * `statement` is the human-readable form. `value` and `unit` carry the same
 * observation unrounded, so the JSON export can serialize full precision with
 * an explicit unit (D-039) instead of parsing numbers back out of prose.
 */
export interface EvidenceItem {
  readonly kind: 'weight' | 'waist' | 'phase' | 'dataQuality'
  readonly statement: string
  readonly value?: number
  readonly unit?: string
}

/** Something the user should keep an eye on, without acting on it yet. */
export type WatchItem = string

/** An action the engine considered and did not choose, and why not. */
export interface RejectedAlternative {
  readonly action: string
  readonly reason: string
}

export interface RulesEngineOpinion {
  readonly outcome: 'opinion'
  readonly category: RecommendationCategory
  /** Exactly one. Enforced in code, not by ruleset discipline (§4). */
  readonly primaryAction: string
  /** Zero to two, only when clearly justified (D-007). */
  readonly supportingActions: readonly string[]
  readonly confidence: Confidence
  /** Why confidence is what it is, in the engine's own terms. */
  readonly confidenceReason: string
  readonly evidence: readonly EvidenceItem[]
  readonly watchItems: readonly WatchItem[]
  /** Evidence the engine wanted and did not have. */
  readonly missingEvidence: readonly string[]
  readonly rejectedAlternatives: readonly RejectedAlternative[]
  readonly triggeredRules: readonly RuleId[]
  readonly engineVersion: string
  readonly evaluatedAt: IsoTimestamp
}

/**
 * The terminal outcome for a phase whose ruleset this build does not implement
 * (D-043).
 *
 * It exists as a guard against restored or stale data — unimplemented phases
 * are not selectable — and it must never be presented as a recommendation or
 * exported as an opinion.
 */
export interface PhaseRulesUnavailable {
  readonly outcome: 'phaseRulesUnavailable'
  readonly phaseType: string
  readonly engineVersion: string
  readonly evaluatedAt: IsoTimestamp
}

/** No active phase, so there is nothing to interpret a week against (D-037). */
export interface RulesContextUnavailable {
  readonly outcome: 'contextUnavailable'
  readonly reason: 'noActivePhase'
  readonly engineVersion: string
  readonly evaluatedAt: IsoTimestamp
}

/**
 * A domain the user is working on that this build cannot judge (D-090).
 *
 * The exact counterpart of `PhaseRulesUnavailable`, one level up, and it exists
 * for the opposite reason. An unimplemented *phase* is unreachable in ordinary
 * use — phases are only selectable once their ruleset exists — so that outcome is
 * a guard against restored data. An unimplemented **domain is completely
 * ordinary**: a user may activate cardio today, and no cardio rules have been
 * approved, so this is what the app has to say about their week.
 *
 * **It carries no primary action, no category and no confidence**, it is never
 * stored as an opinion, and it is never exported as one. What it does carry is
 * `reason` — an honest sentence the user reads, because "Project Sola is not
 * judging this yet" is information rather than an error.
 */
export interface DomainRulesUnavailable {
  readonly outcome: 'domainRulesUnavailable'
  readonly domain: string
  /** Why, in the user's words. Never a stack trace and never blank. */
  readonly reason: string
  readonly engineVersion: string
  readonly evaluatedAt: IsoTimestamp
}

export type RulesOutcome =
  | RulesEngineOpinion
  | PhaseRulesUnavailable
  | RulesContextUnavailable
  | DomainRulesUnavailable

/** Narrowing helper used by the UI and the export layer. */
export function isOpinion(outcome: RulesOutcome): outcome is RulesEngineOpinion {
  return outcome.outcome === 'opinion'
}

/**
 * A persisted opinion (Checkpoint 2.0).
 *
 * Until now the opinion was computed on demand and never stored. It is stored
 * from here on because a finalized review must stay explainable: recomputing it
 * later would run today's engine over today's data and could produce a different
 * answer from the one the user actually read.
 *
 * **Only an `opinion` outcome is stored.** `PhaseRulesUnavailable` and
 * `RulesContextUnavailable` are terminal states, not recommendations, and D-043
 * forbids exporting them as opinions — so the store cannot hold one, and the
 * type says so rather than a comment asking callers to be careful.
 */
export interface OpinionRecord extends RecordEnvelope {
  readonly id: OpinionId
  /** Indexed. The review this opinion was produced for. */
  readonly reviewId: WeeklyReviewId
  /** Indexed. */
  readonly generatedAt: IsoTimestamp
  readonly opinion: RulesEngineOpinion
}
